
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import * as z from "zod";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  full_name: z.string().min(2, "Name must be at least 2 characters"),
  congregation_name: z.string().min(2, "Congregation name must be at least 2 characters"),
});

type ProfileFormFieldsProps = {
  form: UseFormReturn<z.infer<typeof formSchema>>;
};

export function ProfileFormFields({ form }: ProfileFormFieldsProps) {
  const [searchParams] = useSearchParams();
  const [isReadOnly, setIsReadOnly] = useState(false);
  
  useEffect(() => {
    const checkBranding = async () => {
      const braceletId = searchParams.get("code") || searchParams.get("id");
      
      if (braceletId) {
        const cleanId = braceletId.replace(/^GHG/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        
        const { data: bracelet } = await supabase
          .from("bracelets")
          .select("church_code")
          .eq("referral_code", cleanId)
          .maybeSingle();
        
        if (bracelet?.church_code) {
          const { data: churchBranding } = await supabase
            .from("church_branding")
            .select("church_name")
            .eq("church_code", bracelet.church_code)
            .maybeSingle();
          
          if (churchBranding) {
            form.setValue("congregation_name", churchBranding.church_name);
            setIsReadOnly(true);
          }
        }
      }
    };
    
    checkBranding();
  }, [searchParams, form]);

  return (
    <>
      <FormField
        control={form.control}
        name="full_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter your full name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="congregation_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Congregation Name</FormLabel>
            <FormControl>
              <Input 
                placeholder="Enter your congregation name" 
                {...field} 
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </FormControl>
            {isReadOnly && (
              <p className="text-xs text-app-text-secondary mt-1">
                This congregation name is associated with a specific referral code.
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

export { formSchema };
